import * as React from 'react';
import {Calendar} from 'lucide-react';
import {AirbnbDatePickerContent} from './DatePickerContent';
import {
  clampDate,
  formatDateInputValue,
  formatDateValue,
  getMonthLabels,
  normalizeDateValue,
} from './datePicker.utils';
import {useDatePickerWheel} from './useDatePickerWheel';
import {useScreenResize, useCombinedRef} from '../../hooks';
import {DEVICE} from '../../lib/device';
import {cn} from '../../lib/cn';
import {AirbnbFieldTrigger} from '../field-trigger';

type AirbnbDatePickerValue = Date | number | null | undefined | '';

type AirbnbDatePickerProps = {
  label: string;
  topLabel?: string;
  value?: AirbnbDatePickerValue;
  defaultValue?: AirbnbDatePickerValue;
  onChange: (value: Date | null) => void;
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  invalid?: boolean;
  loading?: boolean;
  optional?: boolean | string;
  tooltip?: React.ReactNode;
  helper?: React.ReactNode;
  className?: string;
  name?: string;
  minDate?: Date;
  maxDate?: Date;
  locale?: string;
  mobileTitle?: string;
  doneLabel?: string;
  formatValue?: (date: Date) => string;
};

const DEFAULT_MIN_DATE = new Date(1920, 0, 1);

const AirbnbDatePicker = React.forwardRef<HTMLButtonElement, AirbnbDatePickerProps>(
  (
    {
      label,
      topLabel,
      value,
      defaultValue,
      onChange,
      onBlur,
      placeholder = 'Select a date',
      disabled,
      error,
      invalid,
      loading,
      optional,
      tooltip,
      helper,
      className,
      name,
      minDate,
      maxDate,
      locale = 'en-US',
      mobileTitle,
      doneLabel = 'Done',
      formatValue = formatDateValue,
    },
    ref,
  ) => {
    const {isMatch: isMobile} = useScreenResize(DEVICE.mobileXL);
    const [isOpen, setIsOpen] = React.useState(false);
    const triggerId = React.useId();
    const pickerId = React.useId();
    const labelId = React.useId();
    const valueId = React.useId();
    const helperTextId = React.useId();
    const errorId = React.useId();
    const internalRef = React.useRef<HTMLButtonElement>(null);
    const combinedRef = useCombinedRef(ref, internalRef);
    const monthLabels = React.useMemo(() => getMonthLabels(locale), [locale]);
    const resolvedMinDate = React.useMemo(
      () => normalizeDateValue(minDate) ?? DEFAULT_MIN_DATE,
      [minDate],
    );
    const resolvedMaxDate = React.useMemo(
      () => normalizeDateValue(maxDate) ?? new Date(),
      [maxDate],
    );
    const normalizedValue = React.useMemo(() => normalizeDateValue(value), [value]);
    const normalizedDefaultValue = React.useMemo(
      () => normalizeDateValue(defaultValue),
      [defaultValue],
    );
    const resolvedValue = React.useMemo(
      () =>
        normalizedValue
          ? clampDate(normalizedValue, resolvedMinDate, resolvedMaxDate)
          : null,
      [normalizedValue, resolvedMaxDate, resolvedMinDate],
    );
    const hasValue = Boolean(resolvedValue);
    const isBlocked = Boolean(disabled) || Boolean(loading);
    const triggerError = error ?? invalid;
    const {
      dayIndex,
      dayListRef,
      dayScrollTop,
      days,
      draftDate,
      handleColumnKeyDown,
      handleColumnScroll,
      handleOptionSelect,
      monthIndex,
      monthListRef,
      months,
      monthScrollTop,
      yearIndex,
      yearListRef,
      yearScrollTop,
      years,
    } = useDatePickerWheel({
      isOpen,
      value: resolvedValue,
      defaultValue: normalizedDefaultValue,
      minDate: resolvedMinDate,
      maxDate: resolvedMaxDate,
    });

    const handleOpenChange = React.useCallback(
      (nextOpen: boolean) => {
        if (isBlocked && nextOpen) return;

        setIsOpen(nextOpen);

        if (!nextOpen) {
          internalRef.current?.focus();
        }
      },
      [isBlocked],
    );

    const handleDone = React.useCallback(() => {
      if (isBlocked) return;

      onChange(clampDate(draftDate, resolvedMinDate, resolvedMaxDate));
      handleOpenChange(false);
    }, [
      draftDate,
      handleOpenChange,
      isBlocked,
      onChange,
      resolvedMaxDate,
      resolvedMinDate,
    ]);

    const handleTriggerClick = React.useCallback(() => {
      if (isBlocked) return;

      setIsOpen(true);
    }, [isBlocked]);

    const handleTriggerKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (isBlocked) return;

        if (
          event.key === 'ArrowDown' ||
          event.key === 'ArrowUp' ||
          event.key === 'Enter' ||
          event.key === ' '
        ) {
          event.preventDefault();
          setIsOpen(true);
        }
      },
      [isBlocked],
    );

    React.useEffect(() => {
      if (isBlocked) {
        setIsOpen(false);
      }
    }, [isBlocked]);

    return (
      <div className={cn('relative w-full max-w-[var(--max-field-width)]', className)}>
        {name && (
          <input
            type="hidden"
            name={name}
            value={resolvedValue ? formatDateInputValue(resolvedValue) : ''}
          />
        )}
        <AirbnbFieldTrigger
          id={triggerId}
          ref={combinedRef}
          label={label}
          topLabel={topLabel}
          labelId={labelId}
          valueId={hasValue ? valueId : undefined}
          helperTextId={!hasValue ? helperTextId : undefined}
          errorId={error ? errorId : undefined}
          describedBy={error ? errorId : undefined}
          labelText={topLabel ? placeholder : undefined}
          valueText={resolvedValue ? formatValue(resolvedValue) : undefined}
          placeholder={placeholder}
          disabled={disabled}
          error={triggerError}
          loading={loading}
          optional={optional}
          tooltip={tooltip}
          helper={helper}
          forceLabelText={Boolean(optional) || Boolean(tooltip)}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          onClick={handleTriggerClick}
          onKeyDown={handleTriggerKeyDown}
          onBlur={onBlur}
          trailingAdornment={
            <Calendar
              className="h-5 w-5 text-[var(--chekin-airbnb-ink)]"
              strokeWidth={2}
            />
          }
        />
        <AirbnbDatePickerContent
          baseId={pickerId}
          open={isOpen}
          isMobile={isMobile}
          label={label}
          title={mobileTitle ?? label}
          doneLabel={doneLabel}
          monthLabels={months.map(month => monthLabels[month] ?? '')}
          days={days}
          years={years}
          monthIndex={monthIndex}
          dayIndex={dayIndex}
          yearIndex={yearIndex}
          monthScrollTop={monthScrollTop}
          dayScrollTop={dayScrollTop}
          yearScrollTop={yearScrollTop}
          monthListRef={monthListRef}
          dayListRef={dayListRef}
          yearListRef={yearListRef}
          onOpenChange={handleOpenChange}
          onDone={handleDone}
          onColumnScroll={handleColumnScroll}
          onColumnKeyDown={handleColumnKeyDown}
          onOptionSelect={handleOptionSelect}
        />
      </div>
    );
  },
);
AirbnbDatePicker.displayName = 'AirbnbDatePicker';

export {AirbnbDatePicker};
export type {AirbnbDatePickerProps, AirbnbDatePickerValue};
